import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion'; // Importing Framer Motion
import {
    Search,
    Sparkles,
    Layers3,
    ReceiptText,
    Cloud,
    Headphones,
    SlidersHorizontal,
    LineChart
} from "lucide-react";

const Pricing = () => {
    const stageRef = useRef(null);
    const svgRef = useRef(null);
    const wrapperRef = useRef(null);

    // 1. Responsive Scaling & Height Management (Your Original Logic)
    useEffect(() => {
        const handleResize = () => {
            if (wrapperRef.current) {
                const width = window.innerWidth;
                const baseSize = 760;
                if (width < baseSize) {
                    const scale = (width - 40) / baseSize;
                    wrapperRef.current.style.transform = `scale(${scale})`;
                    const newHeight = baseSize * scale;
                    wrapperRef.current.style.height = `${newHeight + 150}px`;
                } else {
                    wrapperRef.current.style.transform = `scale(1)`;
                    wrapperRef.current.style.height = 'auto';
                }
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 2. Dynamic Radial Layout Logic (Your Original Math)
    useEffect(() => {
        const stage = stageRef.current;
        const svg = svgRef.current;
        if (!stage || !svg) return;

        svg.innerHTML = '';
        const dots = stage.querySelectorAll('.nc-dot');
        dots.forEach(d => d.remove());

        const W = 760, H = 760, cx = 380, cy = 380;
        const orbR = 80;
        // const cardW = 178, cardH = 110;
        const cardW = 178, cardH = 150;
        const angles = [-90, -45, 0, 45, 90, 135, 180, 225];
        const radius = window.innerWidth > 1200 ? 480 : 400;

        const cards = stage.querySelectorAll('.node-card');
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svg.appendChild(defs);

        cards.forEach((card, i) => {
            const a = (angles[i] * Math.PI) / 180;
            const nx = cx + radius * Math.cos(a);
            const ny = cy + radius * Math.sin(a);

            let left = nx - cardW / 2;
            let top = ny - cardH / 2;

            if (left < 2) left = 2;
            if (left + cardW > W - 2) left = W - cardW - 2;
            if (top < 2) top = 2;
            if (top + cardH > H - 2) top = H - cardH - 2;

            card.style.left = left + 'px';
            card.style.top = top + 'px';

            const cardCX = left + cardW / 2;
            const cardCY = top + cardH / 2;
            const dx = cardCX - cx;
            const dy = cardCY - cy;
            const absSlope = Math.abs(dy / dx);
            const rectSlope = cardH / cardW;

            let intersectX, intersectY;
            if (absSlope <= rectSlope) {
                intersectX = cx + (dx > 0 ? (dx - cardW / 2) : (dx + cardW / 2));
                intersectY = cy + (dx > 0 ? (dx - cardW / 2) : (dx + cardW / 2)) * (dy / dx);
            } else {
                intersectY = cy + (dy > 0 ? (dy - cardH / 2) : (dy + cardH / 2));
                intersectX = cx + (dy > 0 ? (dy - cardH / 2) : (dy + cardH / 2)) * (dx / dy);
            }

            const dotEl = document.createElement('div');
            dotEl.className = 'nc-dot ' + (i < 4 ? 'orange' : 'teal');
            dotEl.style.cssText = `left:${intersectX - 6}px;top:${intersectY - 6}px;`;
            stage.appendChild(dotEl);

            const dist = Math.sqrt(dx * dx + dy * dy);
            const ex = cx + (dx / dist) * orbR;
            const ey = cy + (dy / dist) * orbR;
            const col = i < 4 ? '#f73400' : '#36e3bf';
            const gId = `lg${i}`;

            const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            grad.setAttribute('id', gId);
            grad.setAttribute('gradientUnits', 'userSpaceOnUse');
            grad.setAttribute('x1', ex); grad.setAttribute('y1', ey);
            grad.setAttribute('x2', intersectX); grad.setAttribute('y2', intersectY);
            grad.innerHTML = `<stop offset="0%" stop-color="${col}" stop-opacity="0.8"/><stop offset="100%" stop-color="${col}" stop-opacity="0"/>`;
            defs.appendChild(grad);

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', `M ${ex} ${ey} C ${intersectX} ${intersectY}, ${intersectX} ${intersectY}, ${intersectX} ${intersectY}`);
            path.setAttribute('stroke', `url(#${gId})`);
            path.setAttribute('stroke-width', '1.5');
            path.setAttribute('fill', 'none');
            path.setAttribute('stroke-dasharray', '6 5');
            svg.appendChild(path);
        });
    }, []);

    // Framer Motion Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.12, delayChildren: 0.2 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.8, y: 20 },
        visible: { 
            opacity: 1, 
            scale: 1, 
            y: 0,
            transition: { type: "spring", stiffness: 100, damping: 15 }
        }
    };

    const features = [
        { title: "SEO Management", desc: "On-page optimization & keyword architecture.",  icon: <Search size={14} color="#fff" /> },
        { title: "AI Descriptions", desc: "Auto-generated product copy & image enhancement.", icon: <Sparkles size={14} color="#fff" /> },
        { title: "Nth Level Categories", desc: "Nested categories with smart internal linking.",  icon: <Layers3 size={14} color="#fff" /> },
        // { title: "GST Management", desc: "Full GST invoicing & compliant tax reports.",  icon: <ReceiptText size={14} color="#fff" /> },
        { title: "Auto Invoice", desc: "Generate  auto invoices  for every order, helping you manage billing faster and without manual effort.",  icon: <ReceiptText size={14} color="#fff" /> },     
        { title: "Technical Support", desc: "1 year dedicated support — fast & secure.",  icon: <Headphones size={14} color="#fff" /> },
         { title: "Order Management", desc: "Easily manage, track, and update all your customer orders from one powerful and organized dashboard.",  icon: <Headphones size={14} color="#fff" /> },
        { title: "Product Customizer", desc: "Variants, options & live product previews.", icon: <SlidersHorizontal size={14} color="#fff" /> },
        // { title: "Keyword Analysis", desc: "Deep keyword research for high-intent traffic.",  icon: <LineChart size={14} color="#fff" /> },
          { title: "Custom  Design", desc: "We also provide fully customized website design services tailored to for your business",  icon: <Cloud size={14} color="#fff" /> }
    ];

    return (
        <div className="root-template">
            <div className="bg-glow"></div>
            <div className="grid"></div>

            <div className="scaler-wrap" ref={wrapperRef}>
                <div className="wrap">
                    <h2 className="htitle">One Price. <span className="shim">Infinite Potential.</span></h2>
                    <p className="hsub">Every feature your ecommerce store needs in one powerful offer.</p>

                    <motion.div 
                        className="radial-stage" 
                        ref={stageRef}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.2 }}
                    >
                        <div className="ring rg1"></div>
                        <div className="ring rg2"></div>
                        <div className="ring rg3"></div>

                        <svg className="svg-layer" viewBox="0 0 760 760" ref={svgRef}></svg>

                        <div className="orb">
                            {/* <div style={{ fontSize: '15px', letterSpacing: '3px', color: "white" }}>Only at </div> */}
                            {/* <div className="oprice">₹17,500</div> */}
                            {/* <div className="oprice">Axesell</div> */}
                            <div   className='custom-price-logo'>
                            <img src="/assets/images/axeselllogo.webp"  alt="img-logo"  className="custom-img-logo-cde"  loading="lazy" />
                            </div>
                            {/* <div style={{ fontSize: '8px', color: '#36e3bf', marginTop: '5px' }}>ALL-IN-ONE</div> */}
                        </div>

                        {features.map((node, i) => (
                            <motion.div 
                                key={i} 
                                className="node-card mt-4  "
                                variants={cardVariants}
                                whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0,0,0,0.3)" }}
                            >
                                <div className="nc-box">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                                        <div style={{
                                            width: '24px', height: '24px', borderRadius: '6px',
                                            background: i < 4 ? 'rgba(247,52,0,0.1)' : 'rgba(54,227,191,0.1)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {node.icon}
                                        </div>
                                        <span className="nc-title">{node.title}</span>
                                    </div>
                                    <p className="nc-desc">{node.desc}</p>
                                    {/* <div className="nc-pill">{node.pill}</div> */}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Pricing;





